import {
  collection,
  doc,
  getDocs,
  setDoc,
  deleteDoc,
} from 'firebase/firestore'
import type { FoodItem, MenuOption } from '../../types'
import { getFirestoreInstance } from '../firebase'
import { getCurrentPlaceId } from '../placeConfig'
import {
  assignPerCategorySortIndices,
  sortMenuItemsByCategoryOrder,
} from '../menuSortOrder'
import { getCategories, UNCATEGORIZED_CATEGORY_ID } from './categories'

function normalizeOption(opt: MenuOption): MenuOption {
  const choices = opt.choices ?? []
  const prices = opt.choicePrices ?? []
  const choicePrices = choices.map((_, i) => (Array.isArray(prices) && Number.isFinite(prices[i]) ? prices[i]! : 0))
  return { ...opt, choices, choicePrices }
}

function normalizeOptions(
  opts: FoodItem['options'],
  itemId: string,
): MenuOption[] {
  if (!Array.isArray(opts)) return []
  if (opts.length === 0) return []
  const first = opts[0]
  if (
    typeof first === 'object' &&
    first !== null &&
    'id' in first &&
    'type' in first
  ) {
    return (opts as MenuOption[]).map(normalizeOption)
  }
  return (opts as string[]).map((label, i) =>
    normalizeOption({
      id: `legacy-${itemId}-${i}-${label}`,
      label,
      type: 'checklist' as const,
      choices: [label],
    }),
  )
}

function normalizeItem(item: FoodItem & { restaurantId?: string }): FoodItem {
  const { restaurantId: _dropped, ...rest } = item
  const options = normalizeOptions(rest.options, rest.id)
  const sortIndex =
    typeof rest.sortIndex === 'number' && Number.isFinite(rest.sortIndex)
      ? rest.sortIndex
      : undefined
  return { ...rest, options, sortIndex }
}

const menuItemsCacheByPlace: Record<string, FoodItem[]> = {}

const MENU_FIRESTORE_BACKUP_KEY = 'wortelogy-menu-firestore-backup'
const MENU_MAIN_STORAGE_KEY = 'wortelogy-menu'

function saveBackup(placeId: string, items: FoodItem[]): void {
  if (typeof localStorage !== 'undefined') {
    try {
      const json = JSON.stringify(items)
      localStorage.setItem(`${MENU_FIRESTORE_BACKUP_KEY}-${placeId}`, json)
      localStorage.setItem(`${MENU_MAIN_STORAGE_KEY}-${placeId}`, json)
    } catch {
      // ignore quota or other errors
    }
  }
}

function loadBackup(placeId: string): FoodItem[] {
  if (typeof localStorage === 'undefined') return []
  try {
    const raw = localStorage.getItem(`${MENU_FIRESTORE_BACKUP_KEY}-${placeId}`)
    if (!raw) return []
    const parsed = JSON.parse(raw) as FoodItem[]
    return Array.isArray(parsed) ? parsed.map(normalizeItem) : []
  } catch {
    return []
  }
}

function menuItemsRef(placeId: string) {
  return collection(getFirestoreInstance(), 'places', placeId, 'menuItems')
}

function menuItemDoc(placeId: string, itemId: string) {
  return doc(getFirestoreInstance(), 'places', placeId, 'menuItems', itemId)
}

/** Firestore does not allow undefined; convert to null so setDoc succeeds. */
function toFirestoreData(item: FoodItem): Record<string, unknown> {
  function undefinedToNull(value: unknown): unknown {
    if (value === undefined) return null
    if (Array.isArray(value)) return value.map(undefinedToNull)
    if (value !== null && typeof value === 'object') {
      return Object.fromEntries(
        Object.entries(value).map(([k, v]) => [k, undefinedToNull(v)]),
      )
    }
    return value
  }
  return undefinedToNull(item) as Record<string, unknown>
}

export async function primeMenuItemsCache(): Promise<void> {
  const placeId = getCurrentPlaceId()
  try {
    const snap = await getDocs(menuItemsRef(placeId))
    const items = snap.docs.map((d) =>
      normalizeItem({ id: d.id, ...d.data() } as FoodItem),
    )
    const sorted = sortMenuItemsByCategoryOrder(items, getCategories())
    menuItemsCacheByPlace[placeId] = sorted
    if (sorted.length > 0) saveBackup(placeId, sorted)
  } catch (e) {
    console.error('Firestore primeMenuItemsCache failed', e)
  }
  if (!(placeId in menuItemsCacheByPlace) || menuItemsCacheByPlace[placeId].length === 0) {
    const backup = loadBackup(placeId)
    if (backup.length > 0) {
      menuItemsCacheByPlace[placeId] = backup
    } else {
      try {
        const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(`${MENU_MAIN_STORAGE_KEY}-${placeId}`) : null
        if (raw) {
          const parsed = JSON.parse(raw) as FoodItem[]
          if (Array.isArray(parsed) && parsed.length > 0) {
            menuItemsCacheByPlace[placeId] = parsed.map(normalizeItem)
          } else {
            menuItemsCacheByPlace[placeId] = []
          }
        } else {
          menuItemsCacheByPlace[placeId] = []
        }
      } catch {
        menuItemsCacheByPlace[placeId] = []
      }
    }
  }
}

export function getFoodItems(): FoodItem[] {
  const placeId = getCurrentPlaceId()
  return menuItemsCacheByPlace[placeId] ?? []
}

export function saveFoodItems(items: FoodItem[]): void {
  const placeId = getCurrentPlaceId()
  const withSort = assignPerCategorySortIndices(items.map(normalizeItem))
  menuItemsCacheByPlace[placeId] = withSort
  saveBackup(placeId, menuItemsCacheByPlace[placeId])
  ;(async () => {
    const snap = await getDocs(menuItemsRef(placeId))
    const ids = new Set(withSort.map((i) => i.id))
    for (const d of snap.docs) {
      if (!ids.has(d.id)) await deleteDoc(d.ref)
    }
    for (const item of menuItemsCacheByPlace[placeId]) {
      await setDoc(menuItemDoc(placeId, item.id), toFirestoreData(item))
    }
  })().catch((e) => console.error('Firestore saveFoodItems failed', e))
}

function generateFoodId(): string {
  return `F-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

/** Adds item to cache and persists to Firestore. Returns the created item after write completes. */
export async function addFoodItem(item: Omit<FoodItem, 'id'>): Promise<FoodItem> {
  const placeId = getCurrentPlaceId()
  const id = generateFoodId()
  const cache = menuItemsCacheByPlace[placeId] ?? []
  const catKey = item.categoryId ?? UNCATEGORIZED_CATEGORY_ID
  const inCat = cache.filter(
    (i) => (i.categoryId ?? UNCATEGORIZED_CATEGORY_ID) === catKey,
  )
  const maxIdx = inCat.reduce((m, i) => Math.max(m, i.sortIndex ?? -1), -1)
  const full: FoodItem = normalizeItem({
    ...item,
    id,
    sortIndex: maxIdx + 1,
  })
  menuItemsCacheByPlace[placeId] = [...cache, full]
  saveBackup(placeId, menuItemsCacheByPlace[placeId])
  await setDoc(menuItemDoc(placeId, id), toFirestoreData(full))
  return full
}

export function updateFoodItem(
  id: string,
  patch: Partial<FoodItem>,
): FoodItem[] {
  const placeId = getCurrentPlaceId()
  const cache = menuItemsCacheByPlace[placeId] ?? []
  menuItemsCacheByPlace[placeId] = cache.map((i) =>
    i.id === id ? normalizeItem({ ...i, ...patch }) : i,
  )
  const found = menuItemsCacheByPlace[placeId].find((i) => i.id === id)
  if (found) {
    setDoc(menuItemDoc(placeId, id), toFirestoreData(found)).catch((e) =>
      console.error('Firestore updateFoodItem failed', e),
    )
  }
  saveBackup(placeId, menuItemsCacheByPlace[placeId])
  return menuItemsCacheByPlace[placeId]
}

export function deleteFoodItem(id: string): FoodItem[] {
  const placeId = getCurrentPlaceId()
  const cache = menuItemsCacheByPlace[placeId] ?? []
  menuItemsCacheByPlace[placeId] = cache.filter((i) => i.id !== id)
  deleteDoc(menuItemDoc(placeId, id)).catch((e) =>
    console.error('Firestore deleteFoodItem failed', e),
  )
  saveBackup(placeId, menuItemsCacheByPlace[placeId])
  return menuItemsCacheByPlace[placeId]
}

export function clearCategoryFromItems(categoryId: string): void {
  const placeId = getCurrentPlaceId()
  const cache = menuItemsCacheByPlace[placeId] ?? []
  const updated = cache.map((i) =>
    i.categoryId === categoryId ? { ...i, categoryId: undefined } : i,
  )
  menuItemsCacheByPlace[placeId] = updated
  saveFoodItems(updated)
}

export function clearStationFromItems(stationName: string): void {
  const placeId = getCurrentPlaceId()
  const cache = menuItemsCacheByPlace[placeId] ?? []
  const updated = cache.map((i) =>
    i.station === stationName ? { ...i, station: undefined } : i,
  )
  menuItemsCacheByPlace[placeId] = updated
  saveFoodItems(updated)
}

export function replaceStationNameInItems(
  oldName: string,
  newName: string,
): void {
  const placeId = getCurrentPlaceId()
  const cache = menuItemsCacheByPlace[placeId] ?? []
  const updated = cache.map((i) =>
    i.station === oldName ? { ...i, station: newName } : i,
  )
  menuItemsCacheByPlace[placeId] = updated
  saveFoodItems(updated)
}

export function isMenuItemsPrimed(): boolean {
  const placeId = getCurrentPlaceId()
  return placeId in menuItemsCacheByPlace
}

/** Clear in-memory cache so the next user does not see previous account/place data. */
export function clearMenuItemsCache(): void {
  Object.keys(menuItemsCacheByPlace).forEach((k) => delete menuItemsCacheByPlace[k])
}
